import { useMutation, useQueryClient } from '@tanstack/react-query';
import { request } from '@/api/request';
import { toast } from 'sonner';

interface FilmmakerDetails {
  email: string;
  first_name: string;
  last_name: string;
}

interface BankDetails {
  account_name: string;
  account_number: string;
  sort_code: string;
  banking_id: string;
}

interface CreateRecipientParams {
  filmmakerDetails: FilmmakerDetails;
  bankDetails: BankDetails;
  filmmakerId: string;
}

interface SendPayoutParams {
  recipientAccountId: string;
  amount: number;
  paymentId: string;
  description?: string;
}

interface CreateOnboardingLinkParams {
  filmmakerDetails: FilmmakerDetails;
  bankDetails: BankDetails;
  filmmakerId: string;
  return_url?: string;
  refresh_url?: string;
}

interface AccountStatusParams {
  accountId: string;
}

interface UpdateOnboardingStatusParams {
  accountId: string;
}

export const useStripePayouts = () => {
  const queryClient = useQueryClient();

  // Create Stripe recipient
  const createRecipient = useMutation({
    mutationFn: async ({
      filmmakerDetails,
      bankDetails,
      filmmakerId,
    }: CreateRecipientParams) => {
      const data = await request<any>('/stripe-global-payouts', {
        method: 'POST',
        body: JSON.stringify({
          action: 'create_recipient',
          filmmakerDetails,
          bankDetails,
          filmmakerId,
        }),
      });

      if (!data.success)
        throw new Error(data.error || 'Failed to create recipient');

      return data;
    },
    onSuccess: () => {
      toast.success('Stripe recipient created successfully');
      // Refresh payment methods
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to create Stripe recipient', {
        description: error.message,
      });
    },
  });

  // Create Stripe onboarding link
  const createOnboardingLink = useMutation({
    mutationFn: async (params: CreateOnboardingLinkParams) => {
      const data = await request<any>('/stripe-global-payouts', {
        method: 'POST',
        body: JSON.stringify({
          action: 'create_onboarding_link',
          ...params,
        }),
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Stripe account setup initiated');
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
    onError: (error) => {
      toast.error('Failed to setup Stripe account', {
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
    },
  });

  // Check account status
  const checkAccountStatus = useMutation({
    mutationFn: async (params: AccountStatusParams) => {
      const data = await request<any>('/stripe-global-payouts', {
        method: 'POST',
        body: JSON.stringify({
          action: 'get_account_status',
          ...params,
        }),
      });

      return data;
    },
    onError: (error) => {
      toast.error('Failed to check account status', {
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
    },
  });

  // Send payout via Stripe
  const sendPayout = useMutation({
    mutationFn: async ({
      recipientAccountId,
      amount,
      paymentId,
      description,
    }: SendPayoutParams) => {
      const data = await request<any>('/stripe-global-payouts', {
        method: 'POST',
        body: JSON.stringify({
          action: 'send_payout',
          recipientAccountId,
          amount,
          paymentId,
          description,
        }),
      });

      if (!data.success) throw new Error(data.error || 'Failed to send payout');

      return data;
    },
    onSuccess: () => {
      toast.success('Payment sent successfully via Stripe');
      // Refresh admin payment data
      queryClient.invalidateQueries({ queryKey: ['adminPaymentTransactions'] });
      queryClient.invalidateQueries({ queryKey: ['adminPaymentStats'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to send payout', {
        description: error.message,
      });
    },
  });

  // Get payout status
  const getPayoutStatus = useMutation({
    mutationFn: async (transferId: string) => {
      const data = await request<any>('/stripe-global-payouts', {
        method: 'POST',
        body: JSON.stringify({
          action: 'get_payout_status',
          transferId,
        }),
      });

      if (!data.success)
        throw new Error(data.error || 'Failed to get payout status');

      return data;
    },
  });

  // Update onboarding status
  const updateOnboardingStatus = useMutation({
    mutationFn: async (params: UpdateOnboardingStatusParams) => {
      const data = await request<any>('/stripe-global-payouts', {
        method: 'POST',
        body: JSON.stringify({
          action: 'update_onboarding_status',
            ...params,
        }),
      });

      return data;
    },
    onSuccess: (data) => {
      if (data.cleanup_performed) {
        toast.error('Setup abandoned', {
          description:
            'Onboarding was incomplete. Please try adding your bank account again.',
        });
      } else if (data.onboarding_completed) {
        toast.success('Stripe account setup completed!', {
          description: 'You can now receive automatic payments.',
        });
      } else {
        toast.warning('Setup incomplete', {
          description: 'Please complete all required steps in Stripe.',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
      queryClient.invalidateQueries({ queryKey: ['filmmaker-banking'] });
    },
    onError: (error) => {
      toast.error('Failed to check account status', {
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
    },
  });

  return {
    createRecipient,
    createOnboardingLink,
    updateOnboardingStatus,
    checkAccountStatus,
    sendPayout,
    getPayoutStatus,
    isCreatingRecipient: createRecipient.isPending,
    isCreatingOnboardingLink: createOnboardingLink.isPending,
    isUpdatingOnboardingStatus: updateOnboardingStatus.isPending,
    isCheckingAccountStatus: checkAccountStatus.isPending,
    isSendingPayout: sendPayout.isPending,
  };
};
